import { Box, Divider, IconButton, Typography } from '@mui/material'
import EmailIcon from '@mui/icons-material/Email'
import { RichTextEditorDisplay } from '~/features/member-profile/communication'
import { RawTodoRelation } from './PatientToDoService'
/**
 * An email relation, narrowed to exactly what its type predicate checks: the email member of the
 * `content_object` union. The relation's own sibling `content_type` is deliberately not constrained
 * here — nothing verifies it, and nothing reads it.
 */
type RawEmailContent = Extract<RawTodoRelation['content_object'], { content_type: 'email' }>
export type RawEmailRelation = RawTodoRelation & { content_object: RawEmailContent }

type TodoLinkedEmailProps = {
  linkedEmailContent: RawEmailRelation[]
  emailOnClick: () => void
}
export const TodoLinkedEmail = (props: TodoLinkedEmailProps) => {
  const emailAttachments = props.linkedEmailContent.flatMap(
    item => item?.content_object?.attachments ?? [],
  )
  const succeededOrInprogressssAttachment = emailAttachments.filter(
    attachment => attachment?.status !== 'FAILED',
  )
  const failedAttachment = emailAttachments.filter(attachment => attachment?.status === 'FAILED')
  const latestEmail = props.linkedEmailContent[props.linkedEmailContent.length - 1]?.content_object
  const isLatestEmailFailed =
    latestEmail?.sender === 'mailer-daemon@googlemail.com' || latestEmail?.status === 'FAILED'
  const latestEmailContent = latestEmail?.snippet || latestEmail?.message
  const failedColor = isLatestEmailFailed ? 'icons.errorRed' : undefined

  return props.linkedEmailContent.length > 0 ? (
    <Box sx={{ paddingX: 3, mt: 1, flexGrow: 1 }}>
      <Typography sx={{ fontSize: '1.2rem', fontWeight: 'bold', paddingY: '1.5rem' }}>
        Linked Email Threads
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', bgcolor: '#F4F4F4', borderRadius: 3 }}>
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            sx={{
              fontSize: '1.2rem',
              fontWeight: 'bold',
              padding: '1.5rem',
              color: failedColor,
            }}
          >
            {latestEmail?.sender}
          </Typography>
          <Box sx={{ color: failedColor }}>
            <RichTextEditorDisplay content={latestEmailContent ?? ''} maxLength={80} />
          </Box>
          <Box
            sx={{
              padding: '0.5rem',
              alignContent: 'flex-end',
              display: 'flex',
              flexDirection: 'row',
            }}
          >
            <IconButton onClick={props.emailOnClick}>
              <EmailIcon color="info" fontSize="small"></EmailIcon>
              <Typography
                sx={{
                  fontSize: '1.2rem',
                  fontWeight: 'bold',
                  color: '#0077DC',
                  paddingLeft: '0.5rem',
                }}
              >
                {props.linkedEmailContent.length}
              </Typography>
            </IconButton>
          </Box>
        </Box>
        <Divider />
        <Box sx={{ p: 2 }}>
          {emailAttachments && (
            <Typography
              sx={{
                fontSize: '1.2rem',
              }}
            >
              {emailAttachments.length} Attached Files:
            </Typography>
          )}
          {succeededOrInprogressssAttachment.length > 0 && (
            <>
              {/* When there is more than 3 attachments in the email thread (all the email in the case), 
                                list just 1st 3 file name and rest of file as +N files*/}
              {succeededOrInprogressssAttachment.map(
                (attachment, index) =>
                  index < 3 && (
                    <Box
                      key={index}
                      sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', mx: 1 }}
                    >
                      <Box
                        key={index}
                        sx={theme => ({
                          display: 'flex',
                          flexDirection: 'row',
                          width: '5px',
                          height: '5px',
                          borderColor: theme.palette.grey['500'],
                          backgroundColor: theme.palette.grey['500'],
                          borderRadius: '5px',
                          justifyContent: 'space-between',
                          marginX: '5px',
                        })}
                      />
                      <Box
                        key={index}
                        sx={theme => ({
                          display: 'flex',
                          flexDirection: 'column',
                        })}
                      >
                        <Box
                          key={index}
                          sx={theme => ({
                            display: 'flex',
                            flexDirection: 'row',
                            gap: 1,
                          })}
                        >
                          <Typography key={index} sx={theme => ({ fontSize: '12px' })}>
                            {attachment.file_name}{' '}
                          </Typography>
                          <Typography
                            key={index}
                            sx={theme => ({
                              fontSize: '12px',
                              color: theme.palette.error.dark,
                            })}
                          >
                            {attachment.error}
                          </Typography>
                        </Box>
                      </Box>
                      {index == 2 && succeededOrInprogressssAttachment.length > 3 ? (
                        <Typography key={index} sx={theme => ({ fontSize: '12px' })}>
                          ... + {succeededOrInprogressssAttachment.length - 3} files
                        </Typography>
                      ) : (
                        <></>
                      )}
                    </Box>
                  ),
              )}
            </>
          )}
          {failedAttachment.length > 0 && (
            <Box
              sx={theme => ({
                display: 'flex',
                flexDirection: 'column',
                width: 'fit-content',
                alignItems: 'flex-start',
                borderWidth: '1px',
                borderColor: theme.palette.error.dark,
                borderStyle: 'solid',
                borderRadius: '5px',
                paddingY: 1,
                my: 2,
                mx: 1,
              })}
            >
              {failedAttachment.map(
                (attachment, index) =>
                  index < 3 && (
                    <Box
                      key={index}
                      sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', mx: 3 }}
                    >
                      <Box
                        key={index}
                        sx={theme => ({
                          display: 'flex',
                          flexDirection: 'row',
                          width: '5px',
                          height: '5px',
                          borderColor: theme.palette.error.dark,
                          backgroundColor: theme.palette.error.dark,
                          borderRadius: '5px',
                          justifyContent: 'space-between',
                          marginX: '5px',
                        })}
                      />
                      <Typography
                        key={index}
                        sx={theme => ({
                          color: theme.palette.error.dark,
                          fontSize: '12px',
                        })}
                      >
                        {attachment?.error}: {attachment.file_name}
                      </Typography>
                      {index == 2 && failedAttachment.length > 3 ? (
                        <Typography key={index} sx={theme => ({ fontSize: '12px' })}>
                          ... + {failedAttachment.length - 3} files
                        </Typography>
                      ) : (
                        <></>
                      )}
                    </Box>
                  ),
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  ) : (
    <></>
  )
}
